import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { debounce } from 'lodash';
import React, { FC, useEffect, useRef } from 'react';
import { InputWithAddRemove } from 'src/shared/globals';
import { T, validateInputSecurity } from 'src/shared/helpers';
import { validateRequired } from 'src/shared/validation';
import styled from 'styled-components';
import { Icon } from '../../../../../widgets/icon';
import SelectedOptionValue from '../../../state/selectedOptionValue';

interface AttributeOptionListProps {
    isFirstLoad: boolean;
    values: SelectedOptionValue[];
    onAddOption?: (attributeId: string, optionLabel: string) => void;
    onRemoveOption: (attributeId: string, optionId: string, optionLabel: string) => void;
    onEditOption: (newValue: string, attributeId: string, optionId: string) => void;
    isAddVisible?: boolean;
    // When true the attribute has a name but no filled option yet — require at least one.
    requireOption?: boolean;
}

interface AttributeContainerItemProps {
    itemID: string;
    children?: React.ReactNode;
    onClick?: React.MouseEventHandler<HTMLElement>;
    disableDrag?: boolean;
}

// Styled components
const InputListContainer = styled.div`
    > div {
        margin-bottom: 16px;
    }
`;

const AttributeContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 36px;
    grid-column-gap: 20px;
    align-items: start;
    border-bottom: 1px solid ${(props) => props.theme.app.primary_light};
    padding: 20px 0 10px;
    &:first-child {
        padding: 0 0 10px;
    }
`;

const MovedIcon = styled(Icon)<{ movable?: boolean }>`
    vertical-align: middle;
    ${(props) =>
        props.movable &&
        !props.disabled &&
        `&:hover {
          cursor: grab;
          color: ${props.theme.app.secondary}
      }`}
`;

export const AttributeOptionList: FC<AttributeOptionListProps> = ({ values, onAddOption, onRemoveOption, onEditOption, isAddVisible, requireOption }) => {
    const onChangeOptionValue = debounce(onEditOption, 10);
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, optionId: string, optionValueId: string) => {
        onChangeOptionValue(e.currentTarget.value, optionId, optionValueId);
    };
    const prevLengthRef = useRef(values.length);
    useEffect(() => {
        if (values.length > prevLengthRef.current && values.length > 0) {
            const lastOption = values[values.length - 1];
            const lastIndex = values.length - 1;
            // The trailing empty option is appended automatically while typing — don't steal focus in that case.
            const active = document.activeElement as HTMLElement | null;
            const isTypingInList = active?.tagName === 'INPUT' && (active.getAttribute('name') ?? '').startsWith('Attribute-');
            requestAnimationFrame(() => {
                const input = document.querySelector<HTMLInputElement>(`input[name=Attribute-${lastOption.Id}-Option-${lastIndex}]`);
                // Reveal the newly added option field ('nearest' only scrolls when it's off-screen, and
                // scrolling never steals focus from the field being typed in).
                input?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                if (!isTypingInList) input?.focus();
            });
        }
        prevLengthRef.current = values.length;
    }, [values.length]);
    return (
        <InputListContainer>
            {values.length > 0 &&
                values.map((option, index) => {
                    return (
                        <InputWithAddRemove
                            key={`Attribute-${option.Id}-Option-${index}`}
                            name={`Attribute-${option.Id}-Option-${index}`}
                            showAdd={isAddVisible == undefined ? index == values.length - 1 : isAddVisible}
                            showDelete={option.Value.Label.trim() !== ''}
                            message={
                                !validateInputSecurity(option.Value.Label).isValid
                                    ? T._(`Invalid characters detected: ${validateInputSecurity(option.Value.Label).invalidCharacters.join(', ')}`, 'Admin')
                                    : requireOption && index === 0 && validateRequired(option.Value.Label, true)
                                      ? T._('Add at least one option', 'Admin')
                                      : ''
                            }
                            placeholder='Add another value'
                            value={option.Value.Label}
                            onChange={(e) => handleInputChange(e, option.Id, option.Value.Id)}
                            onKeyDown={(e) => e.code == 'Enter' && index == values.length - 1 && onAddOption && onAddOption(option.Id, option.Value.Label)}
                            onKeyUp={(e) => {
                                if (e.code == 'Enter') {
                                    let nextInput = document.querySelector(`input[name=Attribute-${option.Id}-Option-${index + 1}]`);
                                    if (nextInput) (nextInput as HTMLElement).focus();
                                }
                            }}
                            validationKey={`AttributeOptionValidation-${option.Id}-${index}`}
                            addCallback={() => onAddOption && onAddOption(option.Id, option.Value.Label)}
                            removeCallback={() => onRemoveOption(option.Id, option.Value.Id, option.Value.Label)}
                            validate={(value) =>
                                !validateInputSecurity(value).isValid || (requireOption === true && index === 0 && validateRequired(value, true))
                            }
                        />
                    );
                })}
        </InputListContainer>
    );
};

export const AttributeContainerItem: FC<AttributeContainerItemProps> = ({ itemID, children, onClick }) => {
    const { attributes, listeners, setNodeRef, transform } = useSortable({ id: itemID });

    const style = {
        transform: CSS.Transform.toString(transform)
    };

    return (
        <AttributeContainer onClick={onClick} style={style} {...attributes}>
            {/* <div ref={setNodeRef} {...listeners}><MovedIcon movable><MoveIcon /></MovedIcon></div> */}
            {children}
        </AttributeContainer>
    );
};
